import { AppShell, Box, Group, ScrollArea, Stack, Text } from '@mantine/core'
import { useLocation, useNavigate } from 'react-router-dom'
import { RoutePaths } from '@interfaces/routePaths'
import BlueCardMark from '@assets/bluecardMarkColored.svg'
import BlueCardName from '@assets/bluecardNameColored.svg'
import cn from 'classnames'
import classes from './SettingsShellNavigation.module.css'
import { NavFooter } from '@components/NavFooter/NavFooter'
import { useAppSelector } from '@hooks/redux'
import { selectActiveSection } from '@redux/site/siteSlice'

export interface SettingsShellNavigationProps {
  type: 'account' | 'admin'
}

export const SettingsShellNavigation = (
  props: SettingsShellNavigationProps,
) => {
  const { type } = props
  const navigate = useNavigate()
  const activeSection = useAppSelector(selectActiveSection)

  const handleMarkClick = () =>
    navigate(`${RoutePaths.COMMUNITY}/${RoutePaths.COMMUNITY_MEMBERS}`)

  return (
    <>
      <AppShell.Section>
        <Group
          py="lg"
          px="md"
          mt="sm"
          gap="xs"
          align="center"
          onClick={handleMarkClick}
          style={{ cursor: 'pointer' }}
        >
          <img src={BlueCardMark} height={25} />
          <img src={BlueCardName} height={18} />
        </Group>
      </AppShell.Section>
      <AppShell.Section grow component={ScrollArea}>
        <Stack mx="md" pb="xl" mt="md" gap={5}>
          <Box
            className={classes.SettingsShellNavigation__item}
            px="md"
            py={5}
            onClick={handleMarkClick}
          >
            <Text size="md" fw="bold">
              Home
            </Text>
          </Box>
          <Box
            className={classes.SettingsShellNavigation__label}
            px="md"
            py={5}
            mt="md"
          >
            <Text size="xs" fw="bold" tt="uppercase" c="dimmed">
              {type === 'account' ? 'Profile' : 'Admin Settings'}
            </Text>
          </Box>
          {type === 'account' && (
            <>
              <Box
                className={cn(classes.SettingsShellNavigation__item, {
                  [classes.SettingsShellNavigation__itemActive]:
                    activeSection === 'Personal Info',
                })}
                px="md"
                py={5}
                onClick={() =>
                  navigate(
                    `${RoutePaths.ACCOUNT_SETTINGS}/${RoutePaths.ACCOUNT_SETTINGS_PERSONAL_INFO}`,
                  )
                }
              >
                <Text size="md" fw="bold">
                  Personal Info
                </Text>
              </Box>
              <Box
                className={cn(classes.SettingsShellNavigation__item, {
                  [classes.SettingsShellNavigation__itemActive]:
                    activeSection === 'Security',
                })}
                px="md"
                py={5}
                onClick={() =>
                  navigate(
                    `${RoutePaths.ACCOUNT_SETTINGS}/${RoutePaths.ACCOUNT_SETTINGS_SECURITY}`,
                  )
                }
              >
                <Text size="md" fw="bold">
                  Security
                </Text>
              </Box>
              <Box
                className={cn(classes.SettingsShellNavigation__item, {
                  [classes.SettingsShellNavigation__itemActive]:
                    activeSection === 'Privacy',
                })}
                px="md"
                py={5}
                onClick={() =>
                  navigate(
                    `${RoutePaths.ACCOUNT_SETTINGS}/${RoutePaths.ACCOUNT_SETTINGS_PRIVACY}/${RoutePaths.ACCOUNT_SETTINGS_PRIVACY_PROSPECTIVE_CONTACTS}`,
                  )
                }
              >
                <Text size="md" fw="bold">
                  Privacy
                </Text>
              </Box>
              <Box
                className={cn(classes.SettingsShellNavigation__item, {
                  [classes.SettingsShellNavigation__itemActive]:
                    activeSection === 'Notifications',
                })}
                px="md"
                py={5}
                onClick={() =>
                  navigate(
                    `${RoutePaths.ACCOUNT_SETTINGS}/${RoutePaths.ACCOUNT_SETTINGS_NOTIFICATIONS}`,
                  )
                }
              >
                <Text size="md" fw="bold">
                  Notifications
                </Text>
              </Box>
            </>
          )}
          {type === 'admin' && (
            <>
              <Box
                className={cn(classes.SettingsShellNavigation__item, {
                  [classes.SettingsShellNavigation__itemActive]:
                    activeSection === 'Account',
                })}
                px="md"
                py={5}
                onClick={() =>
                  navigate(
                    `${RoutePaths.ADMIN_SETTINGS}/${RoutePaths.ADMIN_SETTINGS_ACCOUNT}/${RoutePaths.ADMIN_SETTINGS_ACCOUNT_ORGANIZATION}`,
                  )
                }
              >
                <Text size="md" fw="bold">
                  Account
                </Text>
              </Box>
              <Box
                className={cn(classes.SettingsShellNavigation__item, {
                  [classes.SettingsShellNavigation__itemActive]:
                    activeSection === 'Locations',
                })}
                px="md"
                py={5}
                onClick={() =>
                  navigate(
                    `${RoutePaths.ADMIN_SETTINGS}/${RoutePaths.ADMIN_SETTINGS_LOCATION_LIST}`,
                  )
                }
              >
                <Text size="md" fw="bold">
                  Locations
                </Text>
              </Box>
              <Box
                className={cn(classes.SettingsShellNavigation__item, {
                  [classes.SettingsShellNavigation__itemActive]:
                    activeSection === 'Staff',
                })}
                px="md"
                py={5}
                onClick={() =>
                  navigate(
                    `${RoutePaths.ADMIN_SETTINGS}/${RoutePaths.ADMIN_SETTINGS_STAFF}`,
                  )
                }
              >
                <Text size="md" fw="bold">
                  Staff
                </Text>
              </Box>
              <Box
                className={cn(classes.SettingsShellNavigation__item, {
                  [classes.SettingsShellNavigation__itemActive]:
                    activeSection === 'Records',
                })}
                px="md"
                py={5}
                onClick={() =>
                  navigate(
                    `${RoutePaths.ADMIN_SETTINGS}/${RoutePaths.ADMIN_SETTINGS_RECORDS}`,
                  )
                }
              >
                <Text size="md" fw="bold">
                  Records
                </Text>
              </Box>
              <Box
                className={cn(classes.SettingsShellNavigation__item, {
                  [classes.SettingsShellNavigation__itemActive]:
                    activeSection === 'Custom Fields',
                })}
                px="md"
                py={5}
                onClick={() =>
                  navigate(
                    `${RoutePaths.ADMIN_SETTINGS}/${RoutePaths.ADMIN_SETTINGS_CUSTOM_FIELDS}`,
                  )
                }
              >
                <Text size="md" fw="bold">
                  Custom Fields
                </Text>
              </Box>
              <Box
                className={cn(classes.SettingsShellNavigation__item, {
                  [classes.SettingsShellNavigation__itemActive]:
                    activeSection === 'Permission Sets',
                })}
                px="md"
                py={5}
                onClick={() =>
                  navigate(
                    `${RoutePaths.ADMIN_SETTINGS}/${RoutePaths.ADMIN_SETTINGS_PERMISSION_SETS_LIST}`,
                  )
                }
              >
                <Text size="md" fw="bold">
                  Permission Sets
                </Text>
              </Box>
              <Box
                className={cn(classes.SettingsShellNavigation__item, {
                  [classes.SettingsShellNavigation__itemActive]:
                    activeSection === 'Exclusive Tags',
                })}
                px="md"
                py={5}
                onClick={() =>
                  navigate(
                    `${RoutePaths.ADMIN_SETTINGS}/${RoutePaths.ADMIN_SETTINGS_EXCLUSIVE_TAGS}`,
                  )
                }
              >
                <Text size="md" fw="bold">
                  Exclusive Tags
                </Text>
              </Box>
              <Box
                className={cn(classes.SettingsShellNavigation__item, {
                  [classes.SettingsShellNavigation__itemActive]:
                    activeSection === 'Stages',
                })}
                px="md"
                py={5}
                onClick={() =>
                  navigate(
                    `${RoutePaths.ADMIN_SETTINGS}/${RoutePaths.ADMIN_SETTINGS_STAGES}`,
                  )
                }
              >
                <Text size="md" fw="bold">
                  Stages
                </Text>
              </Box>
              <Box
                className={cn(classes.SettingsShellNavigation__item, {
                  [classes.SettingsShellNavigation__itemActive]:
                    activeSection === 'Calls',
                })}
                px="md"
                py={5}
                onClick={() =>
                  navigate(
                    `${RoutePaths.ADMIN_SETTINGS}/${RoutePaths.ADMIN_SETTINGS_CALLS}/${RoutePaths.ADMIN_SETTINGS_CALLS_DISPOSITIONS}`,
                  )
                }
              >
                <Text size="md" fw="bold">
                  Calls
                </Text>
              </Box>
              <Box
                className={cn(classes.SettingsShellNavigation__item, {
                  [classes.SettingsShellNavigation__itemActive]:
                    activeSection === 'Incidents',
                })}
                px="md"
                py={5}
                onClick={() =>
                  navigate(
                    `${RoutePaths.ADMIN_SETTINGS}/${RoutePaths.ADMIN_SETTINGS_INCIDENTS}/${RoutePaths.ADMIN_SETTINGS_INCIDENTS_SOURCE}`,
                  )
                }
              >
                <Text size="md" fw="bold">
                  Incidents
                </Text>
              </Box>
              <Box
                className={cn(classes.SettingsShellNavigation__item, {
                  [classes.SettingsShellNavigation__itemActive]:
                    activeSection === 'File Categories',
                })}
                px="md"
                py={5}
                onClick={() =>
                  navigate(
                    `${RoutePaths.ADMIN_SETTINGS}/${RoutePaths.ADMIN_SETTINGS_FILE_CATEGORIES}`,
                  )
                }
              >
                <Text size="md" fw="bold">
                  File Categories
                </Text>
              </Box>
            </>
          )}
        </Stack>
      </AppShell.Section>
      <NavFooter color="light" />
    </>
  )
}
